/* eslint-disable jsx-a11y/heading-has-content */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/jsx-one-expression-per-line */
import React, { useEffect, useState } from 'react';
import { Field, Formik } from 'formik';
import { useHistory, useParams } from 'react-router-dom';
import { StatusCodes } from 'http-status-codes';
import styled from 'styled-components';

import { API_URL, ROLES } from '../../../../utils/constants';
import WhitePad from '../../../../components/common/WhitePad/WhitePad';
import CustomersApi from '../../../../services/api/CustomersApi';
import FormRow from '../../../../components/common/forms/Row/FormRow';
import TextInputComponent from '../../../../components/common/forms/TextInput/TextInputComponent';
import validationSchema from '../validationSchema';
import ButtonComponent from '../../../../components/common/forms/Button/ButtonComponent';
import AutocompleteComponent from '../../../../components/common/forms/Autcomplete/AutocompleteComponent';
import TitleComponent from '../../../../components/common/forms/Title/TitleComponent';
import WarehouseOwnersApi from '~/src/services/api/WarehouseOwnersApi';
import PrimaryButton from '~/src/components/common/forms/Button/PrimaryButton';
import BorderlessButton from '~/src/components/common/forms/Button/BorderlessButton';
import { ContractorUniquenessRequestDto } from '~/src/types/GetContractorDuplicateDto';
import { ContractorUniquenessResponseDto } from '~/src/types/ContractorDuplicateInfoDto';
import ContractorsApi from '~/src/services/api/ContractorsApi';
import DuplicateCheckModal from '~/src/components/common/contractors/DuplicateCheckModal';
import RolesDependent from '~/src/components/common/RolesDependent';
import AutocompletePaginatedComponent from '~/src/components/common/forms/Autcomplete/AutocomplitePaginatedComponent';

export default function WarehouseOwnerEdit(): JSX.Element {
  const { id } = useParams();
  const [isSaveButtonEnabled, setIsSaveButtonEnabled] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [itemData, setItemData] = useState({
    name: '',
    countryId: '',
    address: '',
    uniqueCompanyNumber: '',
    dataArrived: false,
    svilaClientId: '',
  });
  const history = useHistory();

  const uniquenessCheck = async (values: ContractorUniquenessRequestDto, setFieldValue: any) => {
    values.entityType = 'WarehouseOwner';
    let result = null;

    if (id) {
      result = await ContractorsApi.isUniqueForUpdate('WarehouseOwner', id, values);
    } else {
      result = await ContractorsApi.isUniqueForCreate(values);
    }

    if (result?.status === StatusCodes.OK) {
      const resultData: ContractorUniquenessResponseDto = result.data;
      setModalMessage(resultData.message);
      setIsModalOpen(true);

      if (resultData.isPossibleToCreateContractor) {
        setIsSaveButtonEnabled(true);
      } else {
        setIsSaveButtonEnabled(false);
      }

      setFieldValue('contractorId', resultData.contractorId);
    }
  }

  const saveItem = async (values, setSubmitting) => {
    setSubmitting(true);
    let res = null;
    if (id) {
      res = await WarehouseOwnersApi.update(id, values);
    } else {
      res = await WarehouseOwnersApi.create(values);
    }
    if (res.status === StatusCodes.OK) {
      history.push('/warehouse-owners');
    } else {
      setSubmitting(false);
    }
  };

  const getItem = async () => {
    const res = await WarehouseOwnersApi.get(id);
    const newItemData = { ...res.data, dataArrived: true, address: res.data?.contractor?.address, uniqueCompanyNumber: res.data?.contractor?.uniqueCompanyNumber };
    setItemData(newItemData);
  };

  const onDelete = async () => {
    const res = await WarehouseOwnersApi.delete(id);
    if (res.status === StatusCodes.OK) {
      history.push('/warehouse-owners');
    }
  };

  useEffect(() => {
    if (id) {
      getItem(id);
    }
  }, []);

  return (
    <WhitePad>
      <TitleComponent
        onLeftHandler={() => {
          history.push(`/warehouse-owners`);
        }}
        title={`${id ? 'Edit ' : 'Create '} Warehouse Owner`}
      />
      {id && itemData.dataArrived === false ? null : (
        <Formik
          initialValues={{
            name: itemData.name,
            countryId: itemData.contractor?.country?.id || '',
            address: itemData.address,
            uniqueCompanyNumber: itemData.uniqueCompanyNumber,
            contractorId: '',
            svilaClientId: itemData.svilaClientId || '',
          }}
          validationSchema={validationSchema}
          onSubmit={(values, { setSubmitting }) => {
            return saveItem(values, setSubmitting);
          }}
        >
          {({ values, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue, errors }) => (
            <form onSubmit={handleSubmit} autoComplete="off">
              <FormRow>
                <TextInputComponent
                  name="name"
                  label="Name"
                  value={values.name}
                  onBlur={handleBlur}
                  onChange={(event) => {
                    handleChange(event);
                    setIsSaveButtonEnabled(false);
                  }}
                  cols={6}
                />
                <AutocompleteComponent
                  url={`${API_URL}/countries/get`}
                  name="countryId"
                  label="Country"
                  initialValue={values.countryId}
                  onChange={(name, value) => {
                    setFieldValue(name, value);
                    setIsSaveButtonEnabled(false);
                  }}
                  getOptionLabel={(option) => `${option.name}`}
                  errorMessage={errors.countryId}
                />
              </FormRow>
              <FormRow>
                <TextInputComponent
                  name="address"
                  label="Address"
                  value={values.address}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  cols={12}
                />
              </FormRow>
              <FormRow>
                <TextInputComponent
                  name="uniqueCompanyNumber"
                  label="Unique Company Number (VAT)"
                  value={values.uniqueCompanyNumber}
                  onBlur={handleBlur}
                  onChange={(event) => {
                    handleChange(event);
                    setIsSaveButtonEnabled(false);
                  }}
                  cols={12}
                />
              </FormRow>
              <RolesDependent
                roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}>
                <FormRow>
                  <AutocompletePaginatedComponent
                    url={`${API_URL}/svila-clients/get-all`}
                    name="svilaClientId"
                    initialValue={values.svilaClientId}
                    onChange={(name, value) => {
                      setFieldValue(name, value);
                    }}
                    getOptionLabel={(option) => `${option.name}`}
                    errorMessage={errors.svilaClientId}
                    cols={12}
                    label="Svila Client"
                  />
                </FormRow>
              </RolesDependent>
              <ActionWrapper>
                <PrimaryButton
                  variant="primary"
                  isSubmitting={isSubmitting}
                  type="button"
                  title="Check for duplicates"
                  onClick={() => uniquenessCheck(values, setFieldValue)}
                  disabled={!values.name || !values.countryId || !values.uniqueCompanyNumber}
                />
                <PrimaryButton variant="primary"
                  isSubmitting={isSubmitting}
                  type="submit"
                  title="Save"
                  disabled={!isSaveButtonEnabled} />
                {id && itemData.dataArrived === true && (
                  <BorderlessButton marginLeft="30px" size="sm" type="submit" onClick={onDelete} title="Delete" />
                )}
              </ActionWrapper>
            </form>
          )}
        </Formik>
      )}
      <DuplicateCheckModal
        isOpen={isModalOpen}
        message={modalMessage}
        onClose={() => setIsModalOpen(false)}
      />
    </WhitePad>
  );
}

const ActionWrapper = styled.div`
  display: flex;
  align-items: center;
`;
